import React from 'react';
import type { ProcedureResponseProps } from './responseShellTypes';
import ChecklistResponse from './ChecklistResponse';
import FormResponse from './FormResponse';
import ConfirmationResponse from './ConfirmationResponse';
import PhysicalCheckResponse from './PhysicalCheckResponse';
import QuestionnaireResponse from './QuestionnaireResponse';
import RatingResponse from './RatingResponse';
import WalkthroughResponse from './WalkthroughResponse';
import RiskAssessmentResponse from './RiskAssessmentResponse';
import ControlTestResponse from './ControlTestResponse';

/**
 * Fallback for any future / unknown procedure_type that slipped past
 * the schema's enum guard. Kept as a defensive default branch — the 6
 * transactional types all have live bodies as of plan 05b-03 (a + b),
 * the 3 live process-audit types (Walkthrough / RiskAssessment /
 * ControlTest) shipped in plans 05c-05/06/07, and the 2 deferred
 * sub-types (WalkthroughDesign / WalkthroughCompliance) are routed to
 * NotImplementedPlaceholder via the isDeferredToPhase05c early-return.
 */
const PlaceholderBody: React.FC<{ procedureType: string }> = ({ procedureType }) => (
  <div className="not-implemented-placeholder">
    <div className="not-implemented-placeholder-title">
      {procedureType} response — unrecognized procedure type
    </div>
    <div className="not-implemented-placeholder-body">
      No live body is registered for this procedure_type in the dispatcher.
      If you're seeing this, add a case branch to ProcedureResponseDispatcher
      or extend isDeferredToPhase05c if the type belongs to phase 05c.
    </div>
  </div>
);

export interface DispatcherProps extends ProcedureResponseProps {
  procedureType: string;
}

/**
 * Routes a procedure_instance to the correct response body based on
 * procedure_template.procedure_type. After 05c-07 ZERO production types
 * route to NotImplementedPlaceholder — only the 2 Walkthrough sub-types
 * (WalkthroughDesign / WalkthroughCompliance) remain deferred, and those
 * are post-v0.3 per CONTEXT D2 Deferred Ideas.
 *
 *   - 4 sample-bearing transactional types (Checklist, Form, Confirmation,
 *     PhysicalCheck) -> live components shipped by plan 05b-03a.
 *   - 2 sample-less transactional types (Questionnaire, Rating) -> live
 *     components shipped by plan 05b-03b.
 *   - Walkthrough / RiskAssessment / ControlTest -> live bodies shipped by
 *     plans 05c-05 / 05c-06 / 05c-07 respectively. (End of Wave 3.)
 *   - WalkthroughDesign / WalkthroughCompliance -> deferred sub-types,
 *     NotImplementedPlaceholder remains on disk for them.
 *
 * The early-return `isDeferredToPhase05c` guard ensures we NEVER fall through
 * to a transactional branch for a process-audit type — that was the planning
 * bug that the 2026-05-17 revision corrected.
 */
const ProcedureResponseDispatcher: React.FC<DispatcherProps> = ({ procedureType, ...rest }) => {
  switch (procedureType) {
    // Plan 05b-03a — sample-bearing transactional types.
    case 'Checklist':
      return <ChecklistResponse {...rest} />;
    case 'Form':
      return <FormResponse {...rest} />;
    case 'Confirmation':
      return <ConfirmationResponse {...rest} />;
    case 'PhysicalCheck':
      return <PhysicalCheckResponse {...rest} />;
    // Plan 05b-03b — sample-less transactional types.
    case 'Questionnaire':
      return <QuestionnaireResponse {...rest} />;
    case 'Rating':
      return <RatingResponse {...rest} />;
    // Plan 05c-05 — sample-less process-audit type (SOP-step walkthrough).
    // WalkthroughDesign / WalkthroughCompliance are the Test-of-Design and
    // Test-of-Compliance variants of the same SOP-step walkthrough; they reuse
    // WalkthroughResponse (previously deferred to NotImplementedPlaceholder).
    case 'Walkthrough':
    case 'WalkthroughDesign':
    case 'WalkthroughCompliance':
      return <WalkthroughResponse {...rest} />;
    // Plan 05c-06 — sample-less process-audit type (per-risk likelihood/impact/rationale).
    case 'RiskAssessment':
      return <RiskAssessmentResponse {...rest} />;
    // Plan 05c-07 — sample-less process-audit type (per-control Pass/Fail/NA + evidence).
    case 'ControlTest':
      return <ControlTestResponse {...rest} />;
    default:
      return <PlaceholderBody procedureType={procedureType} />;
  }
};

export default ProcedureResponseDispatcher;
